import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  signal,
  viewChild,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { MaterialModule } from "src/app/material.module";
import {
  isBillingOverviewCancellationPending,
  resolveBillingOverviewCancellationEndDateLabel,
  resolveBillingOverviewNextTierLabel,
  resolveBillingOverviewUsageProgressPercent,
} from "../billing-service-overview.mapper";
import { UsageOverviewDialogService } from "../usage-overview-dialog.service";
import { UsageOverviewReverseCancellationService } from "../usage-overview-reverse-cancellation.service";
import { toTranslateParams } from "src/app/shared/helpers/translate-params.helper";
import {
  clampMinimizedPosition,
  getDefaultMinimizedPosition,
  UsageOverviewMinimizedPosition,
} from "./usage-overview-minimized-position.helper";

const DRAG_CLICK_THRESHOLD_PX = 4;

@Component({
  selector: "app-usage-overview-minimized",
  standalone: true,
  imports: [MaterialModule, TranslateModule],
  templateUrl: "./usage-overview-minimized.component.html",
  styleUrls: ["./usage-overview-minimized.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsageOverviewMinimizedComponent {
  private readonly injector = inject(Injector);
  private readonly widgetRef = viewChild<ElementRef<HTMLElement>>("widget");

  protected readonly usageOverviewDialog = inject(UsageOverviewDialogService);
  protected readonly reverseCancellation = inject(
    UsageOverviewReverseCancellationService,
  );
  protected readonly tParams = toTranslateParams;

  protected readonly position = signal<UsageOverviewMinimizedPosition>(
    getDefaultMinimizedPosition(),
  );
  protected readonly isDragging = signal(false);

  private dragOrigin: UsageOverviewMinimizedPosition | null = null;
  private dragPointerStart: { x: number; y: number } | null = null;
  private didDrag = false;

  protected readonly claimCount = computed(
    () => this.usageOverviewDialog.overview()?.usage.claimCount ?? 0,
  );

  protected readonly currentServicePlan = computed(
    () => this.usageOverviewDialog.overview()?.currentServicePlan ?? null,
  );

  protected readonly tierMarkerThreshold = computed(() => {
    const plan = this.currentServicePlan();
    if (!plan) {
      return null;
    }

    return resolveBillingOverviewNextTierLabel(plan);
  });

  protected readonly progressPercent = computed(() => {
    const overview = this.usageOverviewDialog.overview();
    if (!overview) {
      return 0;
    }

    return resolveBillingOverviewUsageProgressPercent(overview);
  });

  protected readonly cancellationPending = computed(() =>
    isBillingOverviewCancellationPending(this.usageOverviewDialog.overview()),
  );

  protected readonly cancellationEndDateLabel = computed(() => {
    const overview = this.usageOverviewDialog.overview();
    if (!overview || !isBillingOverviewCancellationPending(overview)) {
      return null;
    }

    return resolveBillingOverviewCancellationEndDateLabel(overview);
  });

  constructor() {
    effect(() => {
      if (!this.usageOverviewDialog.minimized()) {
        return;
      }

      afterNextRender(
        () => {
          this.applyInitialPosition();
        },
        { injector: this.injector },
      );
    });
  }

  protected onPointerDown(event: PointerEvent): void {
    if (this.shouldIgnoreDrag(event)) {
      return;
    }

    const widget = this.widgetRef()?.nativeElement;
    if (!widget) {
      return;
    }

    widget.setPointerCapture(event.pointerId);
    this.isDragging.set(true);
    this.didDrag = false;
    this.dragPointerStart = { x: event.clientX, y: event.clientY };
    this.dragOrigin = { ...this.position() };
  }

  protected onPointerMove(event: PointerEvent): void {
    if (!this.dragPointerStart || !this.dragOrigin) {
      return;
    }

    const widget = this.widgetRef()?.nativeElement;
    if (!widget) {
      return;
    }

    const deltaX = event.clientX - this.dragPointerStart.x;
    const deltaY = event.clientY - this.dragPointerStart.y;

    if (
      Math.abs(deltaX) > DRAG_CLICK_THRESHOLD_PX ||
      Math.abs(deltaY) > DRAG_CLICK_THRESHOLD_PX
    ) {
      this.didDrag = true;
    }

    const rect = widget.getBoundingClientRect();
    this.position.set(
      clampMinimizedPosition(
        this.dragOrigin.x + deltaX,
        this.dragOrigin.y + deltaY,
        rect.width,
        rect.height,
      ),
    );
  }

  protected onPointerUp(event: PointerEvent): void {
    const widget = this.widgetRef()?.nativeElement;
    if (widget?.hasPointerCapture(event.pointerId)) {
      widget.releasePointerCapture(event.pointerId);
    }

    if (this.didDrag) {
      this.usageOverviewDialog.saveMinimizedPosition(this.position());
    }

    this.isDragging.set(false);
    this.dragPointerStart = null;
    this.dragOrigin = null;
  }

  protected onWidgetClick(): void {
    if (this.didDrag) {
      this.didDrag = false;
      return;
    }

    this.restore();
  }

  protected refresh(event: Event): void {
    event.stopPropagation();
    this.usageOverviewDialog.refresh().subscribe();
  }

  protected keepService(event: Event): void {
    this.reverseCancellation.keepService(event);
  }

  protected close(event: Event): void {
    event.stopPropagation();
    this.usageOverviewDialog.close();
  }

  protected pinToSidenav(event: Event): void {
    event.stopPropagation();
    this.usageOverviewDialog.pinToSidenav();
  }

  private applyInitialPosition(): void {
    const widget = this.widgetRef()?.nativeElement;
    if (!widget) {
      return;
    }

    const rect = widget.getBoundingClientRect();
    const persisted = this.usageOverviewDialog.getPersistedMinimizedPosition();
    const next = persisted ?? getDefaultMinimizedPosition();

    this.position.set(
      clampMinimizedPosition(next.x, next.y, rect.width, rect.height),
    );
  }

  private shouldIgnoreDrag(event: PointerEvent): boolean {
    if (this.usageOverviewDialog.loading()) {
      return true;
    }

    return Boolean(
      (event.target as HTMLElement | null)?.closest(
        ".usage-overview-minimized__icon-button, .usage-overview-minimized__keep-button",
      ),
    );
  }

  private restore(): void {
    this.usageOverviewDialog.restore();
  }
}

